/* -----------------------------------------------------------------------------
| Copyright (c) Jupyter Development Team.
| Distributed under the terms of the Modified BSD License.
|----------------------------------------------------------------------------*/
/**
 * @packageDocumentation
 * @module settingeditor-extension
 */
import { ILabStatus, ILayoutRestorer } from '@jupyterlab/application';
import { Dialog, ICommandPalette, MainAreaWidget, showDialog, showErrorMessage, WidgetTracker } from '@jupyterlab/apputils';
import { IEditorServices } from '@jupyterlab/codeeditor';
import { CommandToolbarButton, IFormRendererRegistry, launchIcon, Toolbar, ToolbarButton } from '@jupyterlab/ui-components';
import { IRenderMimeRegistry } from '@jupyterlab/rendermime';
import { IJSONSettingEditorTracker, ISettingEditorTracker } from '@jupyterlab/settingeditor/lib/tokens';
import { IPluginManager } from '@jupyterlab/pluginmanager';
import { ISettingRegistry } from '@jupyterlab/settingregistry';
import { IStateDB } from '@jupyterlab/statedb';
import { ITranslator } from '@jupyterlab/translation';
import { downloadIcon, fileUploadIcon, saveIcon, settingsIcon, undoIcon } from '@jupyterlab/ui-components';
import { ImportSettingsDialogBodyWidget, ImportSettingsWidget } from './importSettingsWidget';
import { history, historyKeymap } from '@codemirror/commands';
import { keymap } from '@codemirror/view';
const HARDCODED_TO_SKIP = [
    '@jupyterlab/application-extension:context-menu',
    '@jupyterlab/mainmenu-extension:plugin'
];
/**
 * The command IDs used by the setting editor.
 */
var CommandIDs;
(function (CommandIDs) {
    CommandIDs.open = 'settingeditor:open';
    CommandIDs.openJSON = 'settingeditor:open-json';
    CommandIDs.revert = 'settingeditor:revert';
    CommandIDs.save = 'settingeditor:save';
    CommandIDs.exportSettings = 'settingeditor:export';
    CommandIDs.importSettings = 'settingeditor:import';
})(CommandIDs || (CommandIDs = {}));
/**
 * The default setting editor extension.
 */
const plugin = {
    id: '@jupyterlab/settingeditor-extension:form-ui',
    description: 'Adds the interactive settings editor and provides its tracker.',
    requires: [
        ISettingRegistry,
        IStateDB,
        ITranslator,
        IFormRendererRegistry,
        ILabStatus
    ],
    optional: [
        ILayoutRestorer,
        ICommandPalette,
        IJSONSettingEditorTracker,
        IPluginManager
    ],
    autoStart: true,
    provides: ISettingEditorTracker,
    activate
};
/**
 * Activate the setting editor extension.
 */
function activate(app, registry, state, translator, editorRegistry, status, restorer, palette, jsonEditor, pluginManager) {
    const trans = translator.load('jupyterlab');
    const { commands, shell } = app;
    const namespace = 'setting-editor';
    const tracker = new WidgetTracker({
        namespace
    });
    // Handle state restoration.
    if (restorer) {
        void restorer.restore(tracker, {
            command: CommandIDs.open,
            args: widget => ({}),
            name: widget => namespace
        });
    }
    const openUi = async (args) => {
        if (tracker.currentWidget && !tracker.currentWidget.isDisposed) {
            if (!tracker.currentWidget.isAttached) {
                shell.add(tracker.currentWidget, 'main', { type: 'Settings' });
            }
            shell.activateById(tracker.currentWidget.id);
            if (args.query) {
                tracker.currentWidget.content.updateQuery(args.query);
            }
            return;
        }
        const key = plugin.id;
        const { SettingsEditor } = await import('@jupyterlab/settingeditor');
        // Load the settings to get the configurable toSkip list
        const settings = await registry.load(plugin.id);
        const toSkipSetting = settings.get('toSkip').composite;
        const configurableToSkip = Array.isArray(toSkipSetting)
            ? toSkipSetting
            : [];
        // Combine hardcoded and configurable skip lists, avoiding duplicates
        const toSkip = [...new Set([...HARDCODED_TO_SKIP, ...configurableToSkip])];
        const editor = new MainAreaWidget({
            content: new SettingsEditor({
                editorRegistry,
                key,
                registry,
                state,
                commands,
                toSkip,
                translator,
                status,
                query: args.query
            })
        });
        editor.toolbar.addItem('export-settings', new CommandToolbarButton({
            commands,
            id: CommandIDs.exportSettings,
            icon: downloadIcon,
            label: trans.__('Export'),
            caption: trans.__('Export settings to a JSON file')
        }));
        editor.toolbar.addItem('import-settings', new CommandToolbarButton({
            commands,
            id: CommandIDs.importSettings,
            icon: fileUploadIcon,
            label: trans.__('Import'),
            caption: trans.__('Import settings from a JSON file')
        }));
        editor.toolbar.addItem('spacer', Toolbar.createSpacerItem());
        if (pluginManager) {
            editor.toolbar.addItem('open-plugin-manager', new ToolbarButton({
                onClick: async () => {
                    await pluginManager.open();
                },
                icon: launchIcon,
                label: trans.__('Plugin Manager')
            }));
        }
        if (jsonEditor) {
            editor.toolbar.addItem('open-json-editor', new CommandToolbarButton({
                commands,
                id: CommandIDs.openJSON,
                icon: launchIcon,
                label: trans.__('JSON Settings Editor')
            }));
        }
        editor.id = namespace;
        editor.title.icon = settingsIcon;
        editor.title.label = trans.__('Settings');
        editor.title.closable = true;
        void tracker.add(editor);
        shell.add(editor, 'main', { type: 'Settings' });
    };
    commands.addCommand(CommandIDs.open, {
        execute: async (args) => {
            var _a;
            if (args.settingEditorType === 'ui') {
                void commands.execute(CommandIDs.open, { query: (_a = args.query) !== null && _a !== void 0 ? _a : '' });
            }
            else if (args.settingEditorType === 'json') {
                void commands.execute(CommandIDs.openJSON);
            }
            else {
                void registry.load(plugin.id).then(settings => {
                    var _a;
                    settings.get('settingEditorType').composite ===
                        'json'
                        ? void commands.execute(CommandIDs.openJSON)
                        : void openUi({ query: (_a = args.query) !== null && _a !== void 0 ? _a : '' });
                });
            }
        },
        label: args => {
            if (args.label) {
                return args.label;
            }
            return trans.__('Settings Editor');
        },
        describedBy: {
            args: {
                type: 'object',
                properties: {
                    query: {
                        type: 'string',
                        description: trans.__('Search query to filter settings')
                    },
                    settingEditorType: {
                        type: 'string',
                        enum: ['ui', 'json'],
                        description: trans.__('Type of settings editor to open')
                    },
                    label: {
                        type: 'string',
                        description: trans.__('Custom label for the command')
                    }
                }
            }
        }
    });
    if (palette) {
        palette.addItem({
            category: trans.__('Settings'),
            command: CommandIDs.open,
            args: { settingEditorType: 'ui' }
        });
    }
    return tracker;
}
/**
 * The default setting editor extension.
 */
const jsonPlugin = {
    id: '@jupyterlab/settingeditor-extension:plugin',
    description: 'Adds the JSON settings editor and provides its tracker.',
    requires: [
        ISettingRegistry,
        IEditorServices,
        IStateDB,
        IRenderMimeRegistry,
        ILabStatus,
        ITranslator
    ],
    optional: [ILayoutRestorer, ICommandPalette],
    autoStart: true,
    provides: IJSONSettingEditorTracker,
    activate: activateJSON
};
/**
 * Activate the setting editor extension.
 */
function activateJSON(app, registry, editorServices, state, rendermime, status, translator, restorer, palette) {
    const trans = translator.load('jupyterlab');
    const { commands, shell } = app;
    const namespace = 'json-setting-editor';
    const factoryService = editorServices.factoryService;
    const editorFactory = factoryService.newInlineEditor;
    const tracker = new WidgetTracker({
        namespace
    });
    // Handle state restoration.
    if (restorer) {
        void restorer.restore(tracker, {
            command: CommandIDs.openJSON,
            args: widget => ({}),
            name: widget => namespace
        });
    }
    commands.addCommand(CommandIDs.openJSON, {
        execute: async () => {
            if (tracker.currentWidget && !tracker.currentWidget.isDisposed) {
                if (!tracker.currentWidget.isAttached) {
                    shell.add(tracker.currentWidget, 'main', {
                        type: 'Advanced Settings'
                    });
                }
                shell.activateById(tracker.currentWidget.id);
                return;
            }
            const key = plugin.id;
            const when = app.restored;
            const { JsonSettingEditor } = await import('@jupyterlab/settingeditor');
            const editor = new JsonSettingEditor({
                commands: {
                    registry: commands,
                    revert: CommandIDs.revert,
                    save: CommandIDs.save
                },
                editorFactory: options => {
                    var _a;
                    const cmEditor = editorFactory({
                        ...options,
                        extensions: [
                            ...((_a = options.extensions) !== null && _a !== void 0 ? _a : []),
                            history(), // Adds the CM6 undo/redo history extension
                            keymap.of(historyKeymap) // bind Ctrl+Z / Ctrl+Shift+Z
                        ]
                    });
                    return cmEditor;
                },
                key,
                registry,
                rendermime,
                state,
                translator,
                when
            });
            let disposable = null;
            // Notify the command registry when the visibility status of the setting
            // editor's commands change. The setting editor toolbar listens for this
            // signal from the command registry.
            editor.commandsChanged.connect((sender, args) => {
                args.forEach(id => {
                    commands.notifyCommandChanged(id);
                });
                if (editor.canSaveRaw) {
                    if (!disposable) {
                        disposable = status.setDirty();
                    }
                }
                else if (disposable) {
                    disposable.dispose();
                    disposable = null;
                }
                editor.disposed.connect(() => {
                    if (disposable) {
                        disposable.dispose();
                    }
                });
            });
            const container = new MainAreaWidget({
                content: editor
            });
            container.id = namespace;
            container.title.icon = settingsIcon;
            container.title.label = trans.__('Advanced Settings Editor');
            container.title.closable = true;
            void tracker.add(container);
            shell.add(container, 'main', { type: 'Advanced Settings' });
        },
        label: trans.__('Advanced Settings Editor'),
        describedBy: {
            args: {
                type: 'object',
                properties: {}
            }
        }
    });
    if (palette) {
        palette.addItem({
            category: trans.__('Settings'),
            command: CommandIDs.openJSON
        });
    }
    commands.addCommand(CommandIDs.revert, {
        execute: () => {
            var _a;
            (_a = tracker.currentWidget) === null || _a === void 0 ? void 0 : _a.content.revert();
        },
        icon: undoIcon,
        label: trans.__('Revert User Settings'),
        isEnabled: () => { var _a, _b; return (_b = (_a = tracker.currentWidget) === null || _a === void 0 ? void 0 : _a.content.canRevertRaw) !== null && _b !== void 0 ? _b : false; },
        describedBy: {
            args: {
                type: 'object',
                properties: {}
            }
        }
    });
    commands.addCommand(CommandIDs.save, {
        execute: () => { var _a; return (_a = tracker.currentWidget) === null || _a === void 0 ? void 0 : _a.content.save(); },
        icon: saveIcon,
        label: trans.__('Save User Settings'),
        isEnabled: () => { var _a, _b; return (_b = (_a = tracker.currentWidget) === null || _a === void 0 ? void 0 : _a.content.canSaveRaw) !== null && _b !== void 0 ? _b : false; },
        describedBy: {
            args: {
                type: 'object',
                properties: {}
            }
        }
    });
    commands.addCommand(CommandIDs.exportSettings, {
        execute: () => {
            const userSettings = collectUserSettings(registry);
            const jsonContent = JSON.stringify(userSettings, null, 2);
            downloadSettings(jsonContent, 'overrides.json');
        },
        label: trans.__('Export Settings'),
        icon: downloadIcon,
        describedBy: {
            args: {
                type: 'object',
                properties: {}
            }
        }
    });
    commands.addCommand(CommandIDs.importSettings, {
        execute: () => {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = '.json'; // Accept only JSON files
            const JSON_INDENTATION = 4; // Indentation for the JSON file to be uploaded
            fileInput.addEventListener('change', async (event) => {
                var _a;
                const file = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
                if (!file) {
                    return;
                }
                try {
                    const fileContent = await file.text();
                    const importedSettings = JSON.parse(fileContent);
                    // Validate the JSON structure
                    if (typeof importedSettings !== 'object' ||
                        Array.isArray(importedSettings)) {
                        throw new Error('Invalid settings file format');
                    }
                    const errorUploading = [];
                    const applySettings = async (settings) => {
                        var _a;
                        const settingsEntries = Object.entries(importedSettings);
                        for (const [pluginId, pluginSettings] of settingsEntries) {
                            if (typeof pluginSettings === 'object' &&
                                !Array.isArray(pluginSettings)) {
                                try {
                                    await registry.upload(pluginId, JSON.stringify(pluginSettings, undefined, JSON_INDENTATION));
                                }
                                catch (error) {
                                    console.warn(`Failed to save settings for ${pluginId}:`, error);
                                    errorUploading.push(pluginId);
                                }
                            }
                            else {
                                console.warn(`Invalid settings for plugin ${pluginId}. Skipping.`);
                            }
                        }
                        (_a = app.shell.currentWidget) === null || _a === void 0 ? void 0 : _a.close();
                        if (settingsEntries.length) {
                            const successCount = settingsEntries.length - errorUploading.length;
                            const successMessage = trans.__(`Imported settings across ${successCount} ${successCount === 1 ? 'category' : 'categories'} successfully.`);
                            const failureMessage = errorUploading.length
                                ? trans.__(`Failed to upload settings for the following ${errorUploading.length} ${errorUploading.length === 1 ? 'plugin' : 'plugins'}`)
                                : '';
                            const body = new ImportSettingsDialogBodyWidget({
                                successMessage,
                                failureMessage,
                                failedSettings: errorUploading
                            });
                            await showDialog({
                                title: trans.__('Settings Imported'),
                                body,
                                buttons: [Dialog.okButton()]
                            });
                        }
                    };
                    const settingsKeys = Object.keys(importedSettings);
                    const content = new ImportSettingsWidget({
                        importedSettings: settingsKeys,
                        handleImport: applySettings,
                        translator: translator
                    });
                    const widget = new MainAreaWidget({ content });
                    widget.title.label = trans.__('Import Settings');
                    widget.title.icon = fileUploadIcon;
                    app.shell.add(widget, 'main');
                    app.shell.activateById(widget.id);
                }
                catch (error) {
                    await showErrorMessage('Failed to import settings', error);
                }
            });
            // Trigger the file input
            fileInput.click();
        },
        label: trans.__('Import Settings'),
        icon: fileUploadIcon,
        describedBy: {
            args: {
                type: 'object',
                properties: {}
            }
        }
    });
    /**
     * Collect user settings from the registry.
     */
    function collectUserSettings(registry) {
        const userSettings = {};
        for (const [pluginId, plugin] of Object.entries(registry.plugins)) {
            if (plugin) {
                try {
                    if (plugin.raw) {
                        const withoutSingleLineComments = plugin.raw.replace(/\/\/.*$/gm, '');
                        const withoutComments = withoutSingleLineComments.replace(/\/\*[\s\S]*?\*\//g, '');
                        const preferredSettings = JSON.parse(withoutComments);
                        if (Object.keys(preferredSettings).length > 0) {
                            userSettings[pluginId] = preferredSettings;
                        }
                    }
                }
                catch (error) {
                    console.error(`Error loading settings for plugin ${pluginId}:`, error);
                }
            }
        }
        return userSettings;
    }
    /**
     * Trigger a download of the settings as a JSON file.
     */
    function downloadSettings(jsonContent, filename) {
        const blob = new Blob([jsonContent], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    return tracker;
}
export default [plugin, jsonPlugin];
//# sourceMappingURL=index.js.map